/**
 * IBAS programme data — the single source of truth for the /programmes listing,
 * the /programmes/:slug detail pages and the online-learning introduction.
 *
 * SOURCING (ibas-brand skill, hard rule): every institutional fact here is drawn
 * only from the branding folder, https://ibas.edu.eu, the source-derived content
 * bank cache, or the user directly — nothing is invented. Where a figure is not
 * yet verified from those sources it is LEFT OUT (never guessed); the detail page
 * surfaces an "speak to an adviser" path for anything not stated here rather than
 * filling a plausible-sounding placeholder. Slugs match the mega-nav route
 * contract exactly (flexi-mba, flexi-dba, diploma, bba, masters). Images are
 * real IBAS media-library assets (apps/web/public/media).
 *
 * COMPLIANCE (verbatim, brief §5–6): the ACBSP programme mark, badge and the
 * retention count-up appear ONLY where `acbsp === true` (the MBA & DBA routes —
 * flexi-mba, flexi-dba and the on-site masters MBA). The BBA's ACBSP effort is
 * IN PROGRESS, so it carries a text-only status fact and NO mark. Wherever a
 * specialisation or route list appears, the SPECIALISATION_CAVEAT must render
 * alongside it. Tuition and fee amounts stay adviser-routed.
 */

export type ProgrammeType = 'Online' | 'On-site';

/** A single verified fact rendered in the detail page's "at a glance" rail. */
export interface ProgrammeFact {
  /** Short label, e.g. "Duration". */
  label: string;
  /** Verified value, e.g. "Minimum 8 months". */
  value: string;
}

/** A grouped set of Flexi-MBA specialisations (one of the four tracks). */
export interface SpecialisationTrack {
  /** Track name, e.g. "Innovation & Technology". */
  track: string;
  /** Specialisations within the track. */
  options: readonly string[];
}

/** A label→detail pair — powers the entry-requirements and delivery-mode grids. */
export interface LabeledDetail {
  /** Short label, e.g. "Academic" or "Self-study". */
  label: string;
  /** The verified detail sentence. */
  detail: string;
}

/**
 * A grouped block of verified curriculum content (a module list, a set of study
 * structures, or a specialisation-route list). `modules` may hold a single item
 * for note-only or route groups; the Curriculum section requires ≥1 group.
 */
export interface CurriculumGroup {
  /** Group heading, e.g. "Core modules" or "Study structures". */
  group: string;
  /** Optional explanatory note under the heading. */
  note?: string;
  /** The verified items in this group. */
  modules: readonly string[];
}

/** A real IBAS photo tile for a programme's campus/community gallery. */
export interface ProgrammeGalleryItem {
  /** Site-relative path (under /media) to a real IBAS media-library asset. */
  image: string;
  /** Meaningful alt describing what the photo actually shows. */
  alt: string;
  /** Short caption — describes the scene, never an institutional claim. */
  caption: string;
  /** CSS object-position keeping the subject in frame under object-cover. */
  position?: string;
}

export interface Programme {
  /** URL slug — matches the mega-nav hrefs (/programmes/<slug>). */
  slug: string;
  /** Short programme code shown as a gold eyebrow, e.g. "Flexi-MBA". */
  code: string;
  /** Full programme title, e.g. "Master of Business Administration". */
  title: string;
  /** Delivery type — drives the Online / On-site grouping on the listing page. */
  type: ProgrammeType;
  /** Campus/location for on-site programmes (verified: Thalwil, Switzerland). */
  location?: string;
  /** One-line summary shown on cards and under the detail hero. */
  summary: string;
  /**
   * A short intro paragraph for the detail page (institutional content only —
   * drawn from the content bank / live site, never invented).
   */
  intro: string;
  /** Verified "at a glance" facts. Only source-backed values appear here. */
  facts: readonly ProgrammeFact[];
  /** Short verified highlight bullets for the detail page. */
  highlights: readonly string[];
  /**
   * Verified academic + English entry requirements. Absent → the entry panel
   * self-hides (e.g. on-site masters, whose requirements are adviser-routed).
   */
  entryRequirements?: readonly LabeledDetail[];
  /**
   * Verified grouped curriculum (modules / study structures / routes). Absent →
   * the Curriculum section self-hides.
   */
  curriculum?: readonly CurriculumGroup[];
  /**
   * True when a curriculum/specialisation group lists specialisations or routes,
   * so the Curriculum section renders the mandated SPECIALISATION_CAVEAT.
   */
  curriculumHasSpecialisations?: boolean;
  /** Optional lead banner image for the Curriculum section (real media asset). */
  curriculumLead?: { image: string; alt: string };
  /**
   * Verified study/delivery modes (online programmes). Absent → the "Ways to
   * study" grid self-hides (on-site programmes have a single on-campus mode).
   */
  deliveryModes?: readonly LabeledDetail[];
  /** Flexi-MBA only: the 16 specialisations across four tracks. */
  specialisations?: readonly SpecialisationTrack[];
  /**
   * Real IBAS photo tiles for the campus/community gallery. The Gallery section
   * requires ≥3 tiles to mount and never reuses the page hero asset. Absent or
   * <3 → self-hides.
   */
  gallery?: readonly ProgrammeGalleryItem[];
  /**
   * True when the programme carries ACBSP programme accreditation (the MBA & DBA
   * routes only — governs whether the ACBSP mark/badge/retention may appear, per
   * compliance).
   */
  acbsp?: boolean;
  /** 2025 ACBSP student-retention rate, where published (MBA/DBA routes only). */
  retention2025?: string;
  /** Site path (under /media) to the hero/card image — a real IBAS media-library asset. */
  image: string;
  /** Meaningful alt text describing the real photo — not an institutional claim beyond what it shows. */
  alt: string;
  /** Optional CSS object-position for wide crops of the hero/card image (e.g. keeps a portrait subject's face in frame). */
  imagePosition?: string;
}

/**
 * SPECIALISATION_CAVEAT — required-to-publish wherever a specialisation or route
 * list appears (brief §6.1; T&C clauses effective 1 October 2026). Rendered under
 * the Flexi-MBA tracks, the Diploma/BBA curriculum route groups, and appended to
 * the masters "at a glance" note (masters lists 16 specialisations but has no
 * Curriculum/Specialisations section of its own).
 */
export const SPECIALISATION_CAVEAT =
  'Specialisations are indicative and their availability is not guaranteed. A specialisation normally requires a minimum of 10 students to run — IBAS may, at its sole discretion, run one with fewer — and specialisations may be cancelled, combined or substituted.';

/** The five IBAS programmes, in the order they appear in the mega-nav. */
export const PROGRAMMES: readonly Programme[] = [
  {
    slug: 'flexi-mba',
    code: 'Flexi-MBA',
    title: 'Master of Business Administration',
    type: 'Online',
    summary: 'Study fully online with rolling monthly intakes — pace the programme around your career.',
    intro:
      'The Flexi-MBA is IBAS’s fully online, flexible master’s route. Modules open on a rolling monthly basis, so you can begin when it suits you and pace your study around work and life — including the option to pause at any time.',
    facts: [
      { label: 'Award', value: 'Master of Business Administration — General' },
      { label: 'Study mode', value: 'Fully online (Flexi-Learn)' },
      { label: 'Duration', value: 'Minimum 8 months' },
      { label: 'Intakes', value: 'Rolling — monthly modules' },
      { label: 'Credits', value: '60–90 ECTS (minimum 60)' },
      { label: 'QS Online MBA 2026', value: '#19 EMEA · #46 global' },
      {
        label: 'QS Stars 2026',
        value: '5-Star — overall, Business & Management, Teaching, Employability, Inclusiveness',
      },
    ],
    highlights: [
      'Self-study, live online, asynchronous and blended delivery.',
      'Begin on a monthly rolling intake and pause your studies at any time.',
      'Choose from 16 specialisations across four tracks.',
      'One programme, two qualifications — an optional dual (2-in-1) degree through IBAS’s academic partners.',
      'Ranked #19 in the QS Online MBA Rankings 2026 (EMEA) — #46 globally.',
    ],
    entryRequirements: [
      {
        label: 'Academic',
        detail:
          'A Bachelor’s degree or equivalent, or relevant professional experience (subject to assessment).',
      },
      {
        label: 'English',
        detail:
          'IELTS 6.0 or equivalent, prior English-language study, or an approved partner test.',
      },
    ],
    curriculum: [
      {
        group: 'Core modules',
        note: 'Eight required modules — each runs for one month on a rolling basis, with no fixed sequence, so you can begin in any calendar month.',
        modules: [
          'Finance for Strategic Managers',
          'Organizational Behaviour',
          'Personal Development for Leadership and Strategic Management',
          'Research for Strategic Development',
          'Strategic Planning',
          'International Business Environment',
          'International Marketing',
          'Strategic Human Resource Management',
        ],
      },
    ],
    deliveryModes: [
      { label: 'Self-study', detail: 'Asynchronous — study on your own schedule.' },
      { label: 'Live online', detail: 'Synchronous — attend live virtual classes.' },
      { label: 'Blended', detail: 'A mix of self-study and live sessions.' },
      { label: 'On-campus', detail: 'Optional sessions at the IBAS campus in Switzerland.' },
      { label: 'Partner centre', detail: 'Or study through a global partner centre.' },
    ],
    specialisations: [
      {
        track: 'Leadership & Strategy',
        options: ['General', 'International Business', 'Entrepreneurship', 'Management'],
      },
      {
        track: 'Innovation & Technology',
        options: [
          'Artificial Intelligence',
          'IT Project Management',
          'Cybersecurity',
          'Digital Transformation',
          'Data Science',
        ],
      },
      {
        track: 'Finance & Risk',
        options: ['International Finance', 'Business Intelligence'],
      },
      {
        track: 'People & Performance',
        options: [
          'HR Management',
          'Health & Social Care',
          'Hospitality',
          'Marketing',
          'E-commerce',
        ],
      },
    ],
    acbsp: true,
    retention2025: '97.3%',
    image: '/media/images/graduation-events/mba-graduation-audience-g0377.webp',
    alt: 'Rows of IBAS graduates in caps and gowns seated at a graduation ceremony.',
  },
  {
    slug: 'flexi-dba',
    code: 'Flexi-DBA',
    title: 'Doctor of Business Administration',
    type: 'Online',
    summary: 'An online doctoral route built around applied business research.',
    intro:
      'The Flexi-DBA is IBAS’s online doctoral programme, built around applied research. Like the Flexi-MBA it follows the Flexi-Learn model, so you can progress your doctoral study alongside a senior professional role.',
    facts: [
      { label: 'Study mode', value: 'Fully online (Flexi-Learn)' },
      { label: 'Level', value: 'Doctoral' },
      { label: 'Duration', value: '2–3 years' },
      { label: 'Credits', value: '180 ECTS' },
      { label: 'Intakes', value: 'Rolling — monthly modules' },
      { label: 'Focus', value: 'Applied business research' },
    ],
    highlights: [
      'A doctoral route paced around your professional commitments.',
      'Applied research focus — connect scholarship to real business practice.',
      'Delivered fully online through the Flexi-Learn model.',
      'One programme, two qualifications — an optional dual (2-in-1) degree through IBAS’s academic partners.',
      'Graduate by passing all required modules and defending your research dissertation.',
    ],
    entryRequirements: [
      { label: 'Academic', detail: 'A Master’s degree or equivalent qualification.' },
      { label: 'English', detail: 'CEFR Level B2 or equivalent (e.g. IELTS 6.0).' },
    ],
    curriculum: [
      {
        group: 'Taught phase',
        note: 'Ten modules of four to eight weeks each.',
        modules: [
          'Research Philosophy',
          'Managerial Economics',
          'Research Methodology',
          'Advanced Statistics',
          'Multivariate Analysis',
          'Strategic Marketing',
          'International Business',
          'Advanced Accounting and Finance',
          'Organizational Leadership',
          'Strategic Management',
        ],
      },
      {
        group: 'Research & dissertation phase',
        note: 'From problem identification through to a defended dissertation.',
        modules: [
          'Problem identification',
          'Literature review',
          'Proposal preparation',
          'Research design',
          'Pilot study',
          'Data collection & analysis',
          'Interpretation',
          'Dissertation completion',
        ],
      },
    ],
    deliveryModes: [
      { label: 'Live online', detail: 'Synchronous live sessions.' },
      { label: 'Self-study', detail: 'Structured asynchronous study.' },
    ],
    gallery: [
      {
        image: '/media/images/campus/ibas-dba-classroom-session-zurich-3.webp',
        alt: 'IBAS DBA students seated around tables during a relaxed discussion at the Switzerland campus.',
        caption: 'Cohort discussion',
        position: '50% 50%',
      },
      {
        image: '/media/images/campus/ibas-classroom-tableau-presentation.webp',
        alt: 'An IBAS instructor presenting a Tableau software tutorial on a projector screen in a classroom.',
        caption: 'Workshop session',
        position: '50% 50%',
      },
      {
        image: '/media/images/graduation-events/dba-mba-graduates-outdoor-group.webp',
        alt: 'IBAS DBA and MBA graduates in gowns and sashes smiling together outdoors after their ceremony.',
        caption: 'After the ceremony',
        position: '50% 35%',
      },
      {
        image: '/media/images/graduation-events/ibas-dba-graduation-group-photo-zurich.webp',
        alt: 'A large group of IBAS DBA graduates in caps, gowns and gold sashes posing on stage at the graduation ceremony.',
        caption: 'The DBA class',
        position: '50% 45%',
      },
    ],
    acbsp: true,
    retention2025: '94.4%',
    image: '/media/images/graduation-events/dba-graduate-portrait-dsc02137.webp',
    alt: 'An IBAS DBA graduate in academic regalia with a Switzerland DBA sash, photographed outdoors on graduation day.',
    imagePosition: '50% 18%',
  },
  {
    slug: 'diploma',
    code: 'Diploma',
    title: 'Diploma Programme',
    type: 'On-site',
    location: 'Thalwil, Switzerland',
    summary: 'An on-site diploma studied at the IBAS campus in Switzerland.',
    intro:
      'The Diploma of Business Administration is a one-year, on-site diploma (including holidays), studied full-time in English at the IBAS campus in Thalwil, Switzerland. It carries 60 ECTS across eight courses taken over four-week periods.',
    facts: [
      { label: 'Study mode', value: 'On-site, full-time' },
      { label: 'Campus', value: 'Thalwil, Switzerland' },
      { label: 'Award', value: 'Diploma of Business Administration' },
      { label: 'Duration', value: '1 year (incl. holidays)' },
      { label: 'Credits', value: '60 ECTS' },
      { label: 'Language', value: 'English' },
      { label: 'Intakes', value: 'Scheduled' },
    ],
    highlights: [
      'A Diploma of Business Administration — 60 ECTS in one year.',
      'Courses studied over four-week periods, on-site and full-time in English.',
      'Optional Business Management and Hospitality Practice specialisation.',
      'Studied on-site at the IBAS campus in Thalwil, Switzerland.',
    ],
    entryRequirements: [
      { label: 'Academic', detail: 'A High School qualification or equivalent.' },
      { label: 'English', detail: 'CEFR Level B1 or equivalent.' },
    ],
    curriculum: [
      {
        group: 'Core courses',
        note: 'Studied on-site over four-week periods.',
        modules: [
          'People in Organizations',
          'Marketing Principles & Practices',
          'Managing a Work-based Team Project',
          'Personal & Professional Development',
          'The Business Environment',
          'Administrative Services',
        ],
      },
      {
        group: 'Optional specialisation',
        note: 'An optional route adding further courses in applied hospitality.',
        modules: ['Business Management and Hospitality Practice'],
      },
    ],
    curriculumHasSpecialisations: true,
    curriculumLead: {
      image: '/media/images/campus/classroom-session-oral-communication.webp',
      alt: 'Students with laptops attending a business communication class in an IBAS classroom.',
    },
    gallery: [
      {
        image: '/media/images/programmes/students-classroom-laptops-session.webp',
        alt: 'Students working together on laptops in a decorated IBAS classroom.',
        caption: 'In class',
        position: '50% 45%',
      },
      {
        image: '/media/images/campus/classroom-lecture-hands-raised.webp',
        alt: 'Students raising their hands during an IBAS classroom lecture.',
        caption: 'Discussion',
        position: '50% 40%',
      },
      {
        image: '/media/images/people/ibas-students-classroom.webp',
        alt: 'IBAS students at their desks in a sunlit classroom.',
        caption: 'Classroom life',
        position: '50% 45%',
      },
      {
        image: '/media/images/people/student-info-session-classroom.webp',
        alt: 'Students with laptops listening to a presenter in a sunlit IBAS classroom.',
        caption: 'On campus',
        position: '50% 45%',
      },
    ],
    image: '/media/images/campus/classroom-group-students.webp',
    alt: 'Students seated in an IBAS classroom during a lecture.',
  },
  {
    slug: 'bba',
    code: 'BBA',
    title: 'Bachelor of Business Administration',
    type: 'On-site',
    location: 'Thalwil, Switzerland',
    summary: 'An undergraduate business degree (General), studied on-site in Switzerland.',
    intro:
      'The Bachelor of Business Administration (General) is an on-site undergraduate degree studied at the IBAS campus in Thalwil, Switzerland. It carries 180–300 ECTS and can be studied over four years with internships, three years without internships, or an intensive two-year route — across six specialisation routes.',
    facts: [
      { label: 'Study mode', value: 'On-site, full-time' },
      { label: 'Campus', value: 'Thalwil, Switzerland' },
      { label: 'Award', value: 'Bachelor of Business Administration — General' },
      { label: 'Duration', value: '2, 3 or 4 years' },
      { label: 'Credits', value: '180–300 ECTS' },
      { label: 'Intakes', value: 'Scheduled' },
      { label: 'Accreditation', value: 'ACBSP accreditation in progress' },
    ],
    highlights: [
      'Choose from six specialisation routes — General, Finance, Managing People, Operations Management, Sales & Marketing or Hospitality Management.',
      'Study over two, three or four years, with internship pathways.',
      'Graduate by passing all courses, completing internship components and submitting the Capstone Project.',
      'Studied on-site at the IBAS campus in Thalwil, Switzerland.',
    ],
    entryRequirements: [
      {
        label: 'Academic',
        detail: 'A recognised High School Diploma or equivalent secondary qualification.',
      },
      {
        label: 'English',
        detail:
          'CEFR Level B2 — evidenced by international test results, prior English-medium education or equivalent certificates.',
      },
    ],
    curriculum: [
      {
        group: 'Study structures',
        note: 'Choose the route that fits your goals.',
        modules: [
          '4 years — with internships',
          '3 years — without internships',
          '2 years — intensive (calendar year)',
        ],
      },
      {
        group: 'Core courses (years 1–3)',
        modules: [
          'People in Organizations',
          'Marketing Principles & Practices',
          'Managing Communication',
          'Business Ethics',
          'Leadership & Management',
          'Finance for Managers',
          'Risk Management',
          'Customer Relationship Management',
        ],
      },
      {
        group: 'Final-year specialisation courses',
        modules: [
          'Marketing Communications',
          'Human Resource Management',
          'Financial Decision Making',
          'Logistics & Supply Chain Management',
        ],
      },
      {
        group: 'Specialisation routes',
        note: 'Availability is subject to the specialisation policy below.',
        modules: [
          'General',
          'Finance',
          'Managing People',
          'Operations Management',
          'Sales & Marketing',
          'Hospitality Management',
        ],
      },
    ],
    curriculumHasSpecialisations: true,
    gallery: [
      {
        image: '/media/images/campus/ibas-classroom-lecture-session.webp',
        alt: 'Students and an instructor in an IBAS classroom during a lecture.',
        caption: 'Lectures',
        position: '50% 45%',
      },
      {
        image: '/media/images/people/student-group-photo.webp',
        alt: 'Group of students and a lecturer in front of a classroom whiteboard.',
        caption: 'Cohort',
        position: '50% 40%',
      },
      {
        image: '/media/images/people/students-group-discussion-laptops.webp',
        alt: 'Four students at a table with laptops during a discussion.',
        caption: 'Group work',
        position: '50% 45%',
      },
      {
        image: '/media/images/people/students-classroom-candid.webp',
        alt: 'Two students at a desk with a laptop in an IBAS classroom.',
        caption: 'On campus',
        position: '50% 45%',
      },
    ],
    image: '/media/images/people/student-group-outdoor-gathering.webp',
    alt: 'Group of IBAS students standing and sitting under a tree on campus grounds.',
  },
  {
    slug: 'masters',
    code: "Master's",
    title: "Master's Programmes",
    type: 'On-site',
    location: 'Thalwil, Switzerland',
    summary: 'On-site master’s study at the IBAS campus in Switzerland.',
    intro:
      'IBAS offers on-site master’s study as two MBA routes — MBA General, a one-year full-time programme, and MBA Specialised, a two-year full-time programme that includes an internship. Both routes offer 16 specialisations, taught through international case studies and real-world application.',
    facts: [
      { label: 'Study mode', value: 'On-site, full-time' },
      { label: 'Campus', value: 'Thalwil, Switzerland' },
      { label: 'Routes', value: 'MBA General (1 yr) · MBA Specialised (2 yr)' },
      { label: 'Specialisations', value: '16 per route' },
      { label: 'Intakes', value: 'Scheduled' },
    ],
    highlights: [
      'Two routes — MBA General (one year) or MBA Specialised (two years, with an internship).',
      'The one-year General route suits professionals seeking a fast, comprehensive business education.',
      'The two-year Specialised route suits those wanting depth in a specialisation and extended industry engagement.',
      'Both routes offer 16 specialisations, taught through international case studies and real-world application.',
    ],
    gallery: [
      {
        image: '/media/images/campus/instructor-teaching-classroom-group.webp',
        alt: 'IBAS instructor teaching a small group in a classroom with a projected slide.',
        caption: 'Seminars',
        position: '50% 45%',
      },
      {
        image: '/media/images/campus/seminar-classroom-session.webp',
        alt: 'Attendees with laptops during an IBAS session while a facilitator writes on a whiteboard.',
        caption: 'Case study work',
        position: '50% 45%',
      },
      {
        image: '/media/images/people/faculty-students-group-photo-classroom.webp',
        alt: 'IBAS faculty and students together in a classroom with presentation screens.',
        caption: 'Cohort',
        position: '50% 40%',
      },
      {
        image: '/media/images/graduation-events/graduation-certificate-presentation-mba.webp',
        alt: 'Graduate holding an IBAS Master of Business Administration certificate.',
        caption: 'MBA graduation',
        position: '50% 35%',
      },
    ],
    acbsp: true,
    retention2025: '97.3%',
    image: '/media/images/campus/classroom-small-group-discussion.webp',
    alt: 'Instructor guiding a small group discussion among students in an IBAS classroom.',
  },
];

/** Programmes grouped by delivery type — powers the two blocks on the listing page. */
export const PROGRAMMES_BY_TYPE: Record<ProgrammeType, readonly Programme[]> = {
  Online: PROGRAMMES.filter((p) => p.type === 'Online'),
  'On-site': PROGRAMMES.filter((p) => p.type === 'On-site'),
};

/** Look up a single programme by its URL slug (undefined if unknown). */
export function getProgramme(slug: string | undefined): Programme | undefined {
  return PROGRAMMES.find((p) => p.slug === slug);
}

/** Resolve a programme's media-library image path (site-relative, under /media). */
export function programmeImage(programme: Programme, _width = 1400): string {
  return programme.image;
}
